import {
  type ConfiguredModes,
  isDanglingProductAssessmentSource,
} from "./assessment-condition-sources";
import { type ConditionalOperator, isValueRequiredOperator } from "./conditional-display";
import { isDanglingQuizSource } from "./conditional-sources";
import { parseMultiselectValue } from "./field-types";

/**
 * Why a conditional-display rule an author is about to save can never be
 * true — or `null` when it is fine. Shared by every flow and PDF condition
 * dialog so they refuse the same rules in the same order; each dialog maps
 * the code to its message.
 *
 * - `missing_source` — no source field picked;
 * - `missing_quiz` — the quiz the rule names was deleted with its component (QL-13);
 * - `missing_assessment` — the product type or mode is no longer configured on
 *   the flow's collection (QL-361);
 * - `empty_goal_type_selection` — a goal-type rule with nothing ticked (QL-120);
 * - `value_required` — a comparison operator with an empty value.
 */
export type ConditionRuleProblem =
  | "missing_source"
  | "missing_quiz"
  | "missing_assessment"
  | "empty_goal_type_selection"
  | "value_required";

export type ConditionRuleCheckContext = {
  /** The flow's quizzes; `null` while still loading (the dangling check is skipped). */
  quizzes: Array<{ id: string }> | null;
  /** Assessment types configured on the flow's collection; `null` while still loading. */
  assessmentTypes: ConfiguredModes[] | null;
};

export function findConditionRuleProblem(
  rule: { sourceFieldId: string; operator: ConditionalOperator; value: string },
  context: ConditionRuleCheckContext,
): ConditionRuleProblem | null {
  const { sourceFieldId, operator, value } = rule;
  if (!sourceFieldId) return "missing_source";
  if (context.quizzes && isDanglingQuizSource(sourceFieldId, context.quizzes)) {
    return "missing_quiz";
  }
  if (
    context.assessmentTypes &&
    isDanglingProductAssessmentSource(sourceFieldId, context.assessmentTypes)
  ) {
    return "missing_assessment";
  }
  if ((operator === "in" || operator === "not_in") && parseMultiselectValue(value).length === 0) {
    return "empty_goal_type_selection";
  }
  if (isValueRequiredOperator(operator) && !value.trim()) return "value_required";
  return null;
}
